import { useParams } from 'react-router-dom';
import { SEOHead } from '../components/seo/SEOHead';
import { useServiceDetail } from '../hooks/useServices';
import { PricingTable } from '../components/service/PricingTable';
import { BenefitsList, EligibilityList } from '../components/service/BenefitsList';
import { DocumentChecklist } from '../components/service/DocumentChecklist';
import { ProcessSteps } from '../components/service/ProcessSteps';
import { FAQAccordion } from '../components/home/FAQAccordion';
import { RelatedServices } from '../components/service/RelatedServices';
import { ConsultCTA } from '../components/service/ConsultCTA';
import { Spinner } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Link } from 'react-router-dom';
import { buildFaqSchema, buildBreadcrumbSchema } from '../lib/seo-utils';

export default function ServiceDetail() {
  const { slug } = useParams<{ slug: string }>();
  const { data, isLoading, error } = useServiceDetail(slug);

  if (isLoading) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <Spinner className="h-8 w-8 text-seal-blue" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="mx-auto max-w-xl px-4 py-20 text-center">
        <h1 className="font-display text-2xl font-bold text-ink">Service Not Found</h1>
        <p className="mt-2 text-ink/60">This service may have been moved or is no longer available.</p>
        <Link to="/services" className="mt-6 inline-block">
          <Button>Browse All Services</Button>
        </Link>
      </div>
    );
  }

  const { service, availableLocations } = data;

  // handleSelectPlan / booking navigation intentionally removed here —
  // see lib/bookingFlags.ts. Every path that used to call this now
  // points at /consult instead.

  return (
    <>
      <SEOHead
        title={service.seoTitle ?? `${service.title} — Bharat Consultancy`}
        description={service.seoDescription ?? service.shortDescription}
        canonicalPath={`/services/${service.slug}`}
        jsonLd={[
          buildBreadcrumbSchema([
            { name: 'Home', url: 'https://bharat-consultancy.com/' },
            { name: 'Services', url: 'https://bharat-consultancy.com/services' },
            { name: service.title, url: `https://bharat-consultancy.com/services/${service.slug}` },
          ]),
          ...(service.faqs?.length ? [buildFaqSchema(service.faqs)] : []),
        ]}
      />

      {/* Hero */}
      <section className="border-b border-line bg-white py-12">
        <div className="mx-auto max-w-4xl px-4 sm:px-6">
          {/* Starting price eyebrow removed per client request (see
              lib/bookingFlags.ts) — service.startingPrice is still on
              the record, just not surfaced here. */}
          <p className="font-mono text-xs uppercase tracking-widest text-ledger-green">Service</p>
          <h1 className="mt-2 font-display text-3xl font-bold text-ink sm:text-4xl">{service.title}</h1>
          <p className="mt-3 max-w-2xl text-lg text-ink/65">{service.shortDescription}</p>
        </div>
      </section>

      <div className="mx-auto max-w-4xl space-y-14 px-4 py-12 sm:px-6">
        {/* Overview */}
        {service.fullContent && (
          <section>
            <h2 className="font-display text-2xl font-bold text-ink">Overview</h2>
            <p className="mt-4 leading-relaxed text-ink/75">{service.fullContent}</p>
          </section>
        )}

        {/* Benefits */}
        <section>
          <h2 className="font-display text-2xl font-bold text-ink">Benefits</h2>
          <div className="mt-4">
            <BenefitsList
              content={
                service.benefits ??
                `- Expert handling of all paperwork\n- Transparent, upfront pricing\n- Fast turnaround time\n- Dedicated support throughout the process`
              }
            />
          </div>
        </section>

        {/* Eligibility */}
        <section>
          <h2 className="font-display text-2xl font-bold text-ink">Eligibility</h2>
          <div className="mt-4">
            <EligibilityList
              content={
                service.eligibility ??
                `- Valid for individuals and businesses in West Bengal\n- Required documents must be valid and up to date`
              }
            />
          </div>
        </section>

        {/* Required Documents */}
        {service.requiredDocuments && service.requiredDocuments.length > 0 && (
          <section>
            <h2 className="font-display text-2xl font-bold text-ink">Required Documents</h2>
            <div className="mt-4">
              <DocumentChecklist documents={service.requiredDocuments} />
            </div>
          </section>
        )}

        {/* Process Steps */}
        {service.processSteps && service.processSteps.length > 0 && (
          <section>
            <h2 className="font-display text-2xl font-bold text-ink">How It Works</h2>
            <div className="mt-6">
              <ProcessSteps steps={service.processSteps} />
            </div>
          </section>
        )}

        {/* Plan Variants — no prices/CTAs, see PricingTable.tsx */}
        {service.plans && service.plans.length > 0 && (
          <section>
            <h2 className="font-display text-2xl font-bold text-ink">Plans</h2>
            <div className="mt-6">
              <PricingTable plans={service.plans} />
            </div>
            <div className="mt-6">
              <ConsultCTA serviceId={service.id} serviceName={service.title} />
            </div>
          </section>
        )}

        {/* Available locations */}
        {availableLocations.length > 0 && (
          <section>
            <h2 className="font-display text-xl font-bold text-ink">Available in These Cities</h2>
            <div className="mt-3 flex flex-wrap gap-2">
              {availableLocations.map((sl) => (
                <Link
                  key={sl.id}
                  to={`/${sl.urlSlug}`}
                  className="rounded-full border border-line px-4 py-1.5 text-sm text-ink/70 hover:border-seal-blue hover:text-seal-blue"
                >
                  {sl.location.name}
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* FAQs */}
        {service.faqs && service.faqs.length > 0 && (
          <div className="-mx-4 sm:-mx-6">
            <FAQAccordion faqs={service.faqs} title={`${service.title} — FAQs`} />
          </div>
        )}

        {/* Why Choose Us (compact reuse) */}
        <section className="rounded-lg bg-seal-blue/5 p-6 text-center">
          <h2 className="font-display text-xl font-bold text-ink">Why Choose Bharat Consultancy</h2>
          <p className="mx-auto mt-2 max-w-xl text-sm text-ink/65">
            Local expertise, transparent pricing, and a dedicated team that keeps you updated at every step.
          </p>
        </section>

        {/* Related Services */}
        {service.relatedServices && service.relatedServices.length > 0 && (
          <RelatedServices services={service.relatedServices} />
        )}

        {/* Contact CTA */}
        <section className="flex flex-col items-center gap-4 rounded-lg border border-line bg-white p-8 text-center sm:flex-row sm:justify-between sm:text-left">
          <div>
            <h2 className="font-display text-xl font-bold text-ink">Have questions about this service?</h2>
            <p className="mt-1 text-sm text-ink/60">Call or WhatsApp us — we're happy to help.</p>
          </div>
          <div className="flex gap-3">
            <a href="tel:+918170896296">
              <Button variant="outline">Call Us</Button>
            </a>
            {/* "Proceed To Book" replaced with the consultation CTA per
                client request — see lib/bookingFlags.ts. */}
            <Link to={`/consult?serviceId=${service.id}&serviceName=${encodeURIComponent(service.title)}`}>
              <Button variant="accent">Book a 30-Min Session</Button>
            </Link>
          </div>
        </section>
      </div>
    </>
  );
}
